import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect, useMemo } from "react";
import warning from "../../resources/icons/red-warning.svg";
import BlueWarning from "../../resources/icons/blue-warning.svg";
import { useTranslation } from "react-i18next";
import DynamicForm from "../../common/DynamicForm/DynamicForm";
import { POST, GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import DOMPurify from "dompurify";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";

const DYN_VERIFY_MODAL = "dynamic-verification-approval-modal";

export default function DynamicVerificationApprovalModal({
  isModalOpen,
  toggleModal,
  actionType,
  transaction_id,
  apiData,
  defaultFields = [],
  onClose,
  isLlmPolling = false,
}) {
  const { t } = useTranslation();
  const [issuerComments, setIssuerComments] = useState("");
  const [formValues, setFormValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);

  useEffect(() => {
    if (apiData) {
      const initialValues = {};
      Object.keys(apiData).forEach((key) => {
        initialValues[key] = apiData[key] ?? "";
      });
      setFormValues(initialValues);
    }

    if (isModalOpen) {
      setStep(1);
      setHtmlContent("");
    }
  }, [apiData, isModalOpen]);

  function closeModal() {
    if (isLlmPolling) return;
    toggleModal(false);
    setIssuerComments("");
    setFormValues({});
    setFormErrors({});
    setStep(1);
    setHtmlContent("");
    if (onClose && !isSubmitting) {
      onClose();
    }
  }

  const handleFormChange = (values) => {
    setFormValues(values);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    Object.keys(formValues).forEach((key) => {
      const value = formValues[key];
      const fieldConfig = defaultFields.find((field) => field.key === key);

      if (fieldConfig?.is_required === true) {
        if (value === null || value === undefined || value === "") {
          errors[key] = t("verificationApprovals:field_required");
          isValid = false;
          return;
        }

        const stringValue = String(value);
        if (stringValue.trim() === "") {
          errors[key] = t("verificationApprovals:field_required");
          isValid = false;
        }
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const isFormValid = useMemo(() => {
    let valid = true;
    defaultFields.forEach((fieldConfig) => {
      if (fieldConfig?.is_required === true) {
        const value = formValues[fieldConfig.key];
        if (value === null || value === undefined || value === "") {
          valid = false;
        } else if (String(value).trim() === "") {
          valid = false;
        }
      }
    });
    return valid;
  }, [formValues, defaultFields]);

  const handleSaveAndContinue = async () => {
    if (!validateForm()) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:please_fill_all_fields"),
      });
      return;
    }

    setIsSaving(true);
    try {
      const processedFormValues = {};
      Object.keys(formValues).forEach((key) => {
        let value = formValues[key];

        const fieldConfig = defaultFields.find((field) => field.key === key);

        if (fieldConfig?.data_type === "date" && value) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = date.toISOString();
            }
          } catch (e) {
            console.error("Date conversion error:", e);
          }
        }

        if (
          fieldConfig?.data_type === "number" &&
          value !== "" &&
          !isNaN(value) &&
          !isNaN(parseFloat(value))
        ) {
          value = parseFloat(value);
        }

        processedFormValues[key] = value;
      });

      const llmReportBody = {
        llm_report_data: processedFormValues,
      };

      const llmResponse = await POST(
        `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
        llmReportBody,
      );

      if (!llmResponse.ok) {
        const errorData = await llmResponse.json();
        throw new Error(
          errorData.message ||
            t("verificationApprovals:failed_save_verification_details"),
        );
      }

      Toast({
        type: "success",
        title: t("verificationApprovals:success"),
        message: t("verificationApprovals:verification_details_saved"),
      });

      if (actionType === "Approved") {
        setIsLoadingHtml(true);
        const htmlResponse = await GET(
          `${EntityConstants.GET_VERIFICATION_REPORT_EMAIL_TEMPLATE}?transaction_id=${transaction_id}`,
        );

        if (!htmlResponse.ok) {
          throw new Error(`HTTP error! status: ${htmlResponse.status}`);
        }

        const htmlText = await htmlResponse.text();
        setHtmlContent(htmlText);
      }

      setStep(2);
    } catch (error) {
      console.error("Error in save and continue:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message:
          error.message ||
          t("verificationApprovals:failed_save_verification_details_error"),
      });
    } finally {
      setIsSaving(false);
      setIsLoadingHtml(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!issuerComments || issuerComments.trim() === "") {
      setFormErrors((prev) => ({
        ...prev,
        comments: t("verificationApprovals:comments_required"),
      }));
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:please_add_comments"),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const body = {
        transaction_id: transaction_id,
        verification_approval_status: actionType,
        issuer_comments: issuerComments.trim(),
      };

      const response = await POST(
        EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
        body,
      );

      if (response.ok) {
        Toast({
          type: "success",
          title: t("verificationApprovals:transaction_updated"),
          message: `${t(
            "verificationApprovals:transaction_has_been",
          )} ${actionType.toLowerCase()} ${t(
            "verificationApprovals:successfully",
          )}!`,
        });

        toggleModal(false);

        setTimeout(() => {
          if (onClose) {
            onClose(true);
          }
        }, 300);
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message:
            errorData.message ||
            t("verificationApprovals:there_was_error_updating_status"),
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:error_updating_transaction_status"),
      });
      setIsSubmitting(false);
    }
  };

  const getConfirmationMessage = () => {
    if (actionType === "Approved") {
      return `${t("verificationApprovals:are_you_sure_approve_request")}`;
    } else {
      return `${t("verificationApprovals:are_you_sure_reject_request")}`;
    }
  };

  const renderStep1 = () => (
    <>
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">
          {t("verificationApprovals:verification_details")}
        </h4>
        <div className="bg-white p-4 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
          {isLlmPolling ? (
            <div className="flex items-center justify-center py-10">
              <SmallCFLoader text={t("verificationApprovals:processing")} />
            </div>
          ) : (
            <DynamicForm
              formData={apiData}
              defaultFields={defaultFields}
              onFormChange={handleFormChange}
              initialValues={formValues}
              testIdPrefix={`${DYN_VERIFY_MODAL}-form`}
            />
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-4">
        <button
          type="button"
          id={`${DYN_VERIFY_MODAL}-step1-cancel`}
          onClick={closeModal}
          disabled={isSaving || isLlmPolling}
          className="py-2.5 px-5 text-sm hover:bg-gray-200 font-medium text-gray-900 focus:outline-none bg-cfSecondary rounded-lg border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("verificationApprovals:cancel")}
        </button>
        <button
          type="button"
          id={`${DYN_VERIFY_MODAL}-step1-save-continue`}
          onClick={handleSaveAndContinue}
          disabled={isSaving || isLlmPolling || !isFormValid}
          className={`flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-4 transition-colors ${
            isSaving || isLlmPolling || !isFormValid
              ? "bg-blue-300 opacity-70 cursor-not-allowed"
              : "bg-cfPrimaryButton hover:bg-blue-700 focus:ring-blue-300"
          }`}
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {t("verificationApprovals:processing")}
            </>
          ) : (
            t("verificationApprovals:save_and_continue")
          )}
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      {actionType === "Approved" && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3">
            {t("verificationApprovals:email_preview")}
          </h4>
          <div className="bg-white p-4 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
            {isLoadingHtml ? (
              <div className="flex items-center justify-center h-64">
                <CfLoader />
              </div>
            ) : htmlContent ? (
              <div
                className="html-preview-container overflow-auto"
                id={`${DYN_VERIFY_MODAL}-email-preview`}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(htmlContent),
                }}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {t("verificationApprovals:no_html_content_available")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        <label
          htmlFor="issuerComments"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {actionType === "Approved"
            ? t("verificationApprovals:please_add_comment_approve")
            : t("verificationApprovals:please_provide_reason_rejection")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <textarea
          id={`${DYN_VERIFY_MODAL}-comments`}
          value={issuerComments}
          onChange={(e) => {
            setIssuerComments(e.target.value);
            if (formErrors.comments) {
              setFormErrors((prev) => ({
                ...prev,
                comments: "",
              }));
            }
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
            formErrors.comments
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-blue-500"
          }`}
          rows={3}
          placeholder={
            actionType === "Approved"
              ? t("verificationApprovals:enter_approval_comment")
              : t("verificationApprovals:enter_rejection_reason")
          }
          style={{ fontSize: "0.875rem" }}
          required
        />
        {formErrors.comments && (
          <p
            className="mt-1 text-sm text-red-600"
            id={`${DYN_VERIFY_MODAL}-comments-error`}
          >
            {formErrors.comments}
          </p>
        )}
      </div>

      <div className="mt-6 flex justify-center gap-4">
        <button
          type="button"
          id={`${DYN_VERIFY_MODAL}-step2-back`}
          onClick={actionType === "Approved" ? () => setStep(1) : closeModal}
          disabled={isSubmitting}
          className="py-2.5 px-5 text-sm hover:bg-gray-200 font-medium text-gray-900 focus:outline-none bg-cfSecondary rounded-lg border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("verificationApprovals:back")}
        </button>
        <button
          type="button"
          id={`${DYN_VERIFY_MODAL}-step2-${
            actionType === "Approved" ? "approve" : "reject"
          }`}
          onClick={handleConfirmAction}
          disabled={isSubmitting || !issuerComments.trim()}
          className={`text-cfSecondary focus:ring-4 focus:outline-none font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center transition-colors ${
            isSubmitting || !issuerComments.trim()
              ? "opacity-70 cursor-not-allowed " +
                (actionType === "Approved" ? "bg-blue-300" : "bg-red-300")
              : actionType === "Approved"
                ? "bg-cfPrimaryButton hover:bg-[#2a2da9] focus:ring-blue-300"
                : "bg-[#D94D40] hover:bg-[#b84035] focus:ring-red-300"
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {t("verificationApprovals:processing")}
            </span>
          ) : actionType === "Approved" ? (
            t("verificationApprovals:approve")
          ) : (
            t("verificationApprovals:reject")
          )}
        </button>
      </div>
    </>
  );

  const getStepTitle = () => {
    if (step === 1) {
      return actionType === "Approved"
        ? t("verificationApprovals:approve_with_details")
        : t("verificationApprovals:reject");
    } else {
      return actionType === "Approved"
        ? t("verificationApprovals:approve_request")
        : t("verificationApprovals:reject_request");
    }
  };

  const getStepDescription = () => {
    if (step === 1) {
      return getConfirmationMessage();
    } else {
      if (actionType === "Approved") {
        return t("verificationApprovals:review_email_preview_before_approval");
      } else {
        return t("verificationApprovals:please_add_comments_to_reject");
      }
    }
  };

  if (!actionType || !apiData) {
    return null;
  }

  return (
    <>
      <Transition appear show={isModalOpen ? true : false} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/25 backdrop-blur-md"
              id={`${DYN_VERIFY_MODAL}-backdrop`}
            />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full ${actionType === "Approved" ? "max-w-4xl" : "max-w-md"} transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all relative`}
                  id={`${DYN_VERIFY_MODAL}-panel`}
                >
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    id={`${DYN_VERIFY_MODAL}-close`}
                    onClick={closeModal}
                    disabled={isSubmitting || isSaving || isLlmPolling}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <Dialog.Title
                    as="h3"
                    className="text-lg flex justify-center font-medium leading-6 text-gray-900"
                    id={`${DYN_VERIFY_MODAL}-title`}
                  >
                    <img
                      src={actionType === "Approved" ? BlueWarning : warning}
                      alt="warning"
                    />
                  </Dialog.Title>

                  <div className="mt-2">
                    <p className="text-lg font-medium flex justify-center">
                      {getStepTitle()}
                    </p>

                    <p className="text-sm text-gray-500 mt-2 mb-4 flex justify-center text-center">
                      {getStepDescription()}
                    </p>

                    {step === 1 && actionType === "Approved"
                      ? renderStep1()
                      : renderStep2()}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
